/**
 * shareUtils — encode/decode dashboard filter state to/from URL query params
 * so a user can share the current Revenue Bridging Dashboard view (product,
 * geo, device, dates, alignment, comparison mode, currency) via a link.
 *
 * The recipient's dashboard reads these params on mount, seeds the filters,
 * and auto-applies so they land on the same view.
 */

import type { DashboardFilters } from "../types";
import type { AlignmentType, ComparisonModeType } from "./dateUtils";

/** Short, stable query-param keys for the shareable link. */
const PARAM = {
  product: "product",
  geo: "geo",
  device: "device",
  targetStart: "ts",
  targetEnd: "te",
  alignment: "align",
  comparisonMode: "cmp",
  currency: "cur",
} as const;

const ALIGNMENTS: AlignmentType[] = ["D", "W", "M", "Q", "H", "Y"];
const COMPARISON_MODES: ComparisonModeType[] = [
  "YoY",
  "WoW",
  "MoM",
  "QoQ",
  "HoH",
  "DoD",
  "Custom",
];

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Build an absolute shareable URL for the current applied filters. Preserves
 * the existing `?view=dashboard` deep-link param and any unrelated params
 * already on the URL, then overlays the filter params.
 */
export function buildShareUrl(filters: Partial<DashboardFilters>): string {
  const params = new URLSearchParams(window.location.search);
  params.set("view", "dashboard");
  if (filters.product) params.set(PARAM.product, filters.product);
  if (filters.geo) params.set(PARAM.geo, filters.geo);
  if (filters.device) params.set(PARAM.device, filters.device);
  if (filters.targetStartDate)
    params.set(PARAM.targetStart, filters.targetStartDate);
  if (filters.targetEndDate) params.set(PARAM.targetEnd, filters.targetEndDate);
  if (filters.alignment) params.set(PARAM.alignment, filters.alignment);
  if (filters.comparisonMode)
    params.set(PARAM.comparisonMode, filters.comparisonMode);
  if (filters.currency) params.set(PARAM.currency, filters.currency);
  return `${window.location.origin}${
    window.location.pathname
  }?${params.toString()}`;
}

/**
 * Read filter params from the current URL. Returns a partial DashboardFilters
 * containing only the values that were present and valid, or null if none of
 * the filter params are present (so callers can tell "no shared filters" from
 * "shared with defaults"). Values are validated against the known option sets
 * so a malformed link can't push garbage into the filter state.
 */
export function parseShareUrl(): Partial<DashboardFilters> | null {
  const params = new URLSearchParams(window.location.search);
  const has = Object.values(PARAM).some((k) => params.has(k));
  if (!has) return null;

  const out: Partial<DashboardFilters> = {};

  const product = params.get(PARAM.product);
  if (product === "SP" || product === "SB") out.product = product;

  const geo = params.get(PARAM.geo);
  if (geo) out.geo = geo;

  const device = params.get(PARAM.device);
  if (device) out.device = device;

  const ts = params.get(PARAM.targetStart);
  if (ts && DATE_RE.test(ts)) out.targetStartDate = ts;

  const te = params.get(PARAM.targetEnd);
  if (te && DATE_RE.test(te)) out.targetEndDate = te;

  const align = params.get(PARAM.alignment);
  if (align && ALIGNMENTS.includes(align as AlignmentType))
    out.alignment = align as AlignmentType;

  const cmp = params.get(PARAM.comparisonMode);
  if (cmp && COMPARISON_MODES.includes(cmp as ComparisonModeType))
    out.comparisonMode = cmp as ComparisonModeType;

  const cur = params.get(PARAM.currency);
  if (cur === "USD" || cur === "LOC") out.currency = cur;

  return out;
}
